/**
 * ClienteFormWizard — wizard 4-step pra criar Cliente V2 (R-IMPORT 2.x Fase 3).
 *
 * Steps:
 *   1. Identificação básica (pessoa física/jurídica +
 *      razão social ou nome + documento)
 *   2. Endereço fiscal (com busca automática pelo CEP)
 *   3. Contato + observações
 *   4. Revisão + Salvar
 *
 * Dedup em tempo real: ao digitar um documento válido (Mod-11), debounced GET
 * /api/v1/clientes?search=<documento> e mostra alerta se já existe.
 *
 * C-08 (c): dava pra cadastrar SÓ pessoa jurídica — o banco tem coluna `cpf`
 * desde a mig 393 e o POST já aceitava, mas o formulário não oferecia. Cliente
 * pessoa física acabava cadastrado sem documento nenhum.
 * C-08 (d): a busca pelo CEP era prometida no "?" do campo e não existia.
 *
 * Validações client-side:
 *   - CNPJ/CPF Mod-11 (helpers isValidCNPJ / isValidCPF)
 *   - Email format
 *   - CEP 8 dígitos
 *   - Telefone min 10 dígitos
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Input,
  Label,
  Select,
  SelectOption,
  ErroCampo,
  useToast,
} from '@/components/ui';
import { useDebounce } from '@/hooks/use-debounce';
import { api } from '@/lib/api';
// V-1: validação que fala, em vez de botão desabilitado.
import { useValidacaoForm, type RegraCampo } from '@/lib/validacao-form';
import {
  useClienteV2Actions,
  useLookupCnpj,
  useLookupCep,
  type CepLookupResult,
  type ClienteV2,
  type CnpjLookupResult,
  type CreateClienteV2Input,
} from '@/lib/clientes-v2-store';
import { isValidCNPJ, isValidCPF } from '@/lib/format-doc';
// F4 (14/08): wizard DINÂMICO — marcar etiqueta acrescenta passos com os
// blocos COMPARTILHADOS da ficha (F3). Campo copiado é o desenho errado.
import { CampoCelula } from '@/components/admin/ClienteBlocoEditavel';
import {
  AJUDA_BLOCO_FORNECEDOR,
  AJUDA_BLOCO_FUNCIONARIO,
  CAMPOS_BLOCO_FORNECEDOR,
  CAMPOS_BLOCO_FUNCIONARIO,
  converterRascunhoDeCampo,
  specsExigidosForaDoForm,
} from '@/components/admin/clientes-blocos-spec';
import { useSegmentosClientesDisponiveis } from '@/lib/clientes-v2-store';
import { useExigidosParaEtiquetas } from '@/lib/field-requirements-store';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { ETIQUETA_FUNCIONARIO } from '@/lib/clientes/campos-por-etiqueta';
import {
  LIMITE_RAZAO_SOCIAL_OMIE,
  mensagemRazaoLonga,
  sugerirRazaoAbreviada,
} from '@/lib/clientes/razao-social-omie';

// F4: o passo é um ÍNDICE numa sequência dinâmica — as etiquetas marcadas
// acrescentam passos. O número continua verdadeiro no título (aprovado).
type Step = number;
type PassoKey = 'ident' | 'endereco' | 'contato' | 'funcionario' | 'fornecedor' | 'exigidos' | 'revisao';
const ETIQUETAS_OFERTADAS_FIXAS = ['Cliente', 'Fornecedor', 'Funcionário', 'Transportadora'];
// F4: o que o wizard JÁ mostra com UI própria — exigido fora desta lista (e
// fora dos passos de etiqueta) vira o passo "Campos exigidos". As listas
// (emails/contatos/etiquetas) e o documento têm tratamento próprio.
const CAMPOS_COBERTOS_WIZARD = [
  'razaoSocial',
  'nomeFantasia',
  'documento',
  'telefone',
  'numUnidades',
  'email',
  'emails',
  'contatos',
  'etiquetas',
  'enderecoLogradouro',
  'enderecoNumero',
  'enderecoBairro',
  'enderecoCidade',
  'enderecoUf',
  'enderecoCep',
  ...CAMPOS_BLOCO_FUNCIONARIO.map((c) => c.name),
  ...CAMPOS_BLOCO_FORNECEDOR.map((c) => c.name),
];
/** C-08 (c): pessoa jurídica (CNPJ) × pessoa física (CPF). */
type TipoPessoa = 'juridica' | 'fisica';

export function ClienteFormWizard({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (cliente: ClienteV2) => void;
}) {
  const [step, setStep] = useState<Step>(1);
  // F4: etiquetas do cadastro (definem os passos e a régua) + rascunho dos
  // passos dinâmicos (mesma forma do rascunho da ficha).
  const [etiquetasSelecionadas, setEtiquetasSelecionadas] = useState<string[]>([]);
  const [extras, setExtras] = useState<Record<string, string>>({});
  const { data: segmentosBase = [] } = useSegmentosClientesDisponiveis();
  // RH-MENU (R3): com o RH ligado, funcionário se cadastra pelo RH — a etiqueta
  // (e com ela o passo de funcionário) sai daqui. O servidor recusa junto
  // (`funcionario-pelo-rh`): esconder é conforto, não trava.
  const rhLigado = useMyFeatureFlag('MENU_RH_ENABLED');
  const etiquetasOfertadas = useMemo(() => {
    const set = new Set<string>(ETIQUETAS_OFERTADAS_FIXAS);
    for (const sgm of segmentosBase) set.add(sgm);
    if (rhLigado) set.delete(ETIQUETA_FUNCIONARIO);
    return [...set];
  }, [segmentosBase, rhLigado]);
  // 13/08: obrigatoriedade dinâmica (Configurações › Preenchimento
  // obrigatório, entity 'client'). O servidor revalida — aqui é o asterisco
  // e a mensagem em português no passo certo.
  // F4: a régua agora é a UNIÃO global ∪ etiquetas MARCADAS — o mesmo motor
  // do servidor e da ficha.
  const exigidos = useExigidosParaEtiquetas(etiquetasSelecionadas);
  // F4: exigido que o wizard não mostra fixo (banco, IE, entrega…) vira um
  // passo próprio — a régua manda no formulário, nunca o contrário.
  const specsExtras = useMemo(
    () => specsExigidosForaDoForm(exigidos, CAMPOS_COBERTOS_WIZARD),
    [exigidos],
  );
  const seq = useMemo<PassoKey[]>(
    () => [
      'ident',
      'endereco',
      'contato',
      ...(etiquetasSelecionadas.includes('Funcionário') ? (['funcionario'] as const) : []),
      ...(etiquetasSelecionadas.includes('Fornecedor') ? (['fornecedor'] as const) : []),
      ...(specsExtras.length > 0 ? (['exigidos'] as const) : []),
      'revisao',
    ],
    [etiquetasSelecionadas, specsExtras],
  );
  const passoAtual: PassoKey = seq[step - 1] ?? 'ident';
  const qc = useQueryClient();
  const { create } = useClienteV2Actions();
  const lookupCnpj = useLookupCnpj();
  const lookupCep = useLookupCep();
  const [lookupMsg, setLookupMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [cepMsg, setCepMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  // Form state
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('juridica');
  const [cpf, setCpf] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [enderecoLogradouro, setEnderecoLogradouro] = useState('');
  const [enderecoNumero, setEnderecoNumero] = useState('');
  const [enderecoBairro, setEnderecoBairro] = useState('');
  const [enderecoCidade, setEnderecoCidade] = useState('');
  const [enderecoUf, setEnderecoUf] = useState('');
  const [enderecoCep, setEnderecoCep] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [observacoesOperacionais, setObservacoesOperacionais] = useState('');
  // O4 (Orçamento v2): nº de unidades do condomínio — auto-preenche o
  // "por unidade/mês" dos simuladores do orçamento.
  const [numUnidades, setNumUnidades] = useState('');

  // Dedup state — vale pros DOIS documentos (o cadastro é único por documento
  // normalizado no servidor; avisar antes evita o 409 na cara do usuário).
  const documento = tipoPessoa === 'fisica' ? cpf : cnpj;
  const documentoDebounced = useDebounce(documento, 500);
  const [clienteExistente, setClienteExistente] = useState<ClienteV2 | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setTipoPessoa('juridica');
      setCpf('');
      setCepMsg(null);
      setRazaoSocial('');
      setNomeFantasia('');
      setCnpj('');
      setEnderecoLogradouro('');
      setEnderecoNumero('');
      setEnderecoBairro('');
      setEnderecoCidade('');
      setEnderecoUf('');
      setEnderecoCep('');
      setEmail('');
      setTelefone('');
      setObservacoesOperacionais('');
      setNumUnidades('');
      setEtiquetasSelecionadas([]);
      setExtras({});
      setClienteExistente(null);
      setLookupMsg(null);
    }
  }, [open]);

  // Dedup do documento (CNPJ ou CPF), debounced. A busca do servidor já casa
  // por `documento_normalizado` (só dígitos), então o mesmo endpoint serve pros
  // dois — comparar pelo campo normalizado evita depender da máscara digitada.
  useEffect(() => {
    const digitos = normalize(documentoDebounced);
    const valido =
      tipoPessoa === 'fisica' ? isValidCPF(documentoDebounced) : isValidCNPJ(documentoDebounced);
    if (!open || !documentoDebounced || !valido) {
      setClienteExistente(null);
      return;
    }
    let cancelled = false;
    api
      // ⚠️ 11/08: a lista passou a devolver `{ items, total }` (paginação, no
      // padrão de Produtos). Esta chamada é a TRAVA DE DUPLICADO do wizard —
      // se ela continuasse lendo um array cru, `data.find` viraria
      // `undefined.find` e a trava pararia de avisar EM SILÊNCIO. Que é
      // justamente como nasceu a duplicata do "ceu azul B".
      .get<{ items: ClienteV2[] }>(
        `/api/v1/clientes?search=${encodeURIComponent(documentoDebounced)}`,
      )
      .then((resp) => {
        if (cancelled) return;
        const match = (resp.items ?? []).find(
          (c) =>
            c.documentoNormalizado === digitos ||
            (c.cnpj && normalize(c.cnpj) === digitos) ||
            (c.cpf && normalize(c.cpf) === digitos),
        );
        setClienteExistente(match ?? null);
      })
      .catch(() => {
        if (!cancelled) setClienteExistente(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, documentoDebounced, tipoPessoa]);

  const cnpjStripped = useMemo(() => cnpj.replace(/\D/g, ''), [cnpj]);
  const cnpjEmpty = cnpjStripped.length === 0;
  const cnpjValid = cnpjEmpty || isValidCNPJ(cnpj);
  const cpfStripped = useMemo(() => cpf.replace(/\D/g, ''), [cpf]);
  const cpfEmpty = cpfStripped.length === 0;
  const cpfValid = cpfEmpty || isValidCPF(cpf);
  const emailValid = email.length === 0 || /^\S+@\S+\.\S+$/.test(email);
  const cepValid =
    enderecoCep.length === 0 || enderecoCep.replace(/\D/g, '').length === 8;
  const telefoneValid =
    telefone.length === 0 || telefone.replace(/\D/g, '').length >= 10;

  const toast = useToast();
  const validacao = useValidacaoForm((m) => toast.show(m, { variant: 'error' }));

  const docObrigatorio = exigidos.has('documento');
  // 'emails' é o vocabulário do inventário (lista, "pelo menos um"); 'email'
  // é o nome antigo que pode seguir marcado na base. Os dois exigem o campo.
  const emailObrigatorio = exigidos.has('email') || exigidos.has('emails');
  // F5: a linha "Etiquetas (segmento)" da régua global = "pelo menos uma".
  // Só o wizard sente — o rápido já nasce Cliente e satisfaz sozinho.
  const etiquetaObrigatoria = exigidos.has('etiquetas');
  const telefoneObrigatorio = exigidos.has('telefone');
  const unidadesObrigatorio = exigidos.has('numUnidades');
  const docPreenchido = tipoPessoa === 'fisica' ? !cpfEmpty : !cnpjEmpty;

  const step1Valid =
    razaoSocial.trim().length >= 2 &&
    razaoSocial.trim().length <= LIMITE_RAZAO_SOCIAL_OMIE &&
    (tipoPessoa === 'fisica' ? cpfValid : cnpjValid) &&
    (!docObrigatorio || docPreenchido);
  const step2Valid = cepValid;
  const step3Valid =
    emailValid &&
    telefoneValid &&
    (!emailObrigatorio || email.trim().length > 0) &&
    (!telefoneObrigatorio || telefone.trim().length > 0) &&
    (!unidadesObrigatorio || numUnidades.trim().length > 0);

  /**
   * V-1: o "Avançar →" era `disabled` quando o passo não estava válido — e o
   * wizard ficava mudo. Pior que num modal simples: aqui a pessoa não sabe nem
   * em qual dos campos do passo está o problema.
   *
   * As regras seguem a ordem VISUAL do passo, então o foco cai no primeiro
   * campo que falta de cima para baixo.
   */
  function regrasDoPasso(): RegraCampo[] {
    if (passoAtual === 'ident') {
      return [
        // F5: as etiquetas vêm primeiro porque são o primeiro bloco do passo.
        {
          id: 'wcli-etiquetas',
          ok: !etiquetaObrigatoria || etiquetasSelecionadas.length > 0,
          mensagem: 'Marque pelo menos uma etiqueta — obrigatório pela configuração da empresa.',
        },
        {
          id: 'wcli-razao',
          ok: razaoSocial.trim().length >= 2,
          mensagem: 'Informe a razão social do cliente.',
        },
        {
          id: 'wcli-razao',
          ok: razaoSocial.trim().length <= LIMITE_RAZAO_SOCIAL_OMIE,
          mensagem: mensagemRazaoLonga(razaoSocial.trim().length),
        },
        {
          id: tipoPessoa === 'fisica' ? 'wcli-cpf' : 'wcli-cnpj',
          ok: tipoPessoa === 'fisica' ? cpfValid : cnpjValid,
          mensagem:
            tipoPessoa === 'fisica'
              ? 'O CPF está incompleto ou inválido.'
              : 'O CNPJ está incompleto ou inválido.',
        },
        {
          id: tipoPessoa === 'fisica' ? 'wcli-cpf' : 'wcli-cnpj',
          ok: !docObrigatorio || docPreenchido,
          mensagem: 'Informe o CNPJ ou CPF — obrigatório pela configuração da empresa.',
        },
      ];
    }
    if (passoAtual === 'endereco') {
      return [{ id: 'wcli-cep', ok: cepValid, mensagem: 'O CEP precisa ter 8 dígitos.' }];
    }
    if (passoAtual === 'funcionario' || passoAtual === 'fornecedor') {
      // F4: o passo da etiqueta valida os campos DELE — exigidos pela régua
      // não podem ficar vazios, e todo rascunho tem que converter.
      const campos = passoAtual === 'funcionario' ? CAMPOS_BLOCO_FUNCIONARIO : CAMPOS_BLOCO_FORNECEDOR;
      return campos.map((c) => {
        const bruto = (extras[c.name] ?? '').trim();
        const conv = converterRascunhoDeCampo(c, bruto);
        if (!conv.ok) return { id: `wcli-etq-${c.name}`, ok: false, mensagem: conv.erro };
        return {
          id: `wcli-etq-${c.name}`,
          ok: !(exigidos.has(c.name) && conv.valor == null),
          mensagem: `${c.label} é obrigatório pra esta etiqueta.`,
        };
      });
    }
    if (passoAtual === 'exigidos') {
      // Todos os campos deste passo SÃO exigidos — é por isso que ele existe.
      return specsExtras.map((c) => {
        const bruto = (extras[c.name] ?? '').trim();
        const conv = converterRascunhoDeCampo(c, bruto);
        if (!conv.ok) return { id: `wcli-exig-${c.name}`, ok: false, mensagem: conv.erro };
        return {
          id: `wcli-exig-${c.name}`,
          ok: conv.valor != null,
          mensagem: `${c.label} não pode ficar em branco — é obrigatório pra este cadastro.`,
        };
      });
    }
    if (passoAtual === 'revisao') return [];
    return [
      { id: 'wcli-email', ok: emailValid, mensagem: 'O e-mail está incompleto — confira ou deixe em branco.' },
      {
        id: 'wcli-email',
        ok: !emailObrigatorio || email.trim().length > 0,
        mensagem: 'Informe o e-mail — obrigatório pela configuração da empresa.',
      },
      { id: 'wcli-telefone', ok: telefoneValid, mensagem: 'O telefone precisa ter DDD + número.' },
      {
        id: 'wcli-telefone',
        ok: !telefoneObrigatorio || telefone.trim().length > 0,
        mensagem: 'Informe o telefone — obrigatório pela configuração da empresa.',
      },
      {
        id: 'wcli-unidades',
        ok: !unidadesObrigatorio || numUnidades.trim().length > 0,
        mensagem: 'Informe o nº de unidades — obrigatório pela configuração da empresa.',
      },
    ];
  }

  function handleNext() {
    if (!validacao.validar(regrasDoPasso())) return;
    if (passoAtual === 'ident' && !step1Valid) return;
    if (passoAtual === 'endereco' && !step2Valid) return;
    if (passoAtual === 'contato' && !step3Valid) return;
    if (step < seq.length) setStep(step + 1);
  }

  function handleBack() {
    if (step > 1) setStep((step - 1) as Step);
  }

  function handleSubmit() {
    const input: CreateClienteV2Input = {
      razaoSocial: razaoSocial.trim(),
      nomeFantasia: nomeFantasia.trim() || null,
      // Um cadastro tem UM documento: mandar os dois faria o servidor eleger o
      // CNPJ (precedência do `resolveDocumento`) e o CPF viraria dado órfão.
      cnpj: tipoPessoa === 'fisica' ? null : cnpj.trim() || null,
      cpf: tipoPessoa === 'fisica' ? cpf.trim() || null : null,
      email: email.trim() || null,
      telefone: telefone.trim() || null,
      enderecoLogradouro: enderecoLogradouro.trim() || null,
      enderecoNumero: enderecoNumero.trim() || null,
      enderecoBairro: enderecoBairro.trim() || null,
      enderecoCidade: enderecoCidade.trim() || null,
      enderecoUf: enderecoUf.trim() || null,
      enderecoCep: enderecoCep.trim() || null,
      observacoesOperacionais: observacoesOperacionais.trim() || null,
      numUnidades: numUnidades.trim() !== '' && Number(numUnidades) >= 0 ? Math.round(Number(numUnidades)) : null,
    };
    // F4: campos dos passos dinâmicos, convertidos pelo MESMO motor da ficha.
    const camposDinamicos: Record<string, unknown> = {};
    for (const c of [
      ...(seq.includes('funcionario') ? CAMPOS_BLOCO_FUNCIONARIO : []),
      ...(seq.includes('fornecedor') ? CAMPOS_BLOCO_FORNECEDOR : []),
      ...(seq.includes('exigidos') ? specsExtras : []),
    ]) {
      const conv = converterRascunhoDeCampo(c, (extras[c.name] ?? '').trim());
      if (!conv.ok) {
        toast.show(conv.erro, { variant: 'error' });
        return;
      }
      if (conv.valor !== null) camposDinamicos[c.name] = conv.valor;
    }
    const inputComEtiquetas: CreateClienteV2Input = {
      ...input,
      ...(etiquetasSelecionadas.length > 0 ? { etiquetas: etiquetasSelecionadas } : {}),
      ...camposDinamicos,
    };
    create.mutate(inputComEtiquetas, {
      onSuccess: (cliente) => {
        qc.invalidateQueries({ queryKey: ['v1', 'clientes'] });
        onCreated?.(cliente);
        onClose();
      },
    });
  }

  // CL1.3 — "Buscar dados pelo CNPJ" (BrasilAPI): autopreenche os campos VAZIOS
  // sem sobrescrever o que o usuário já digitou. Falha da API não derruba nada.
  function buscarDadosCnpj() {
    if (!isValidCNPJ(cnpj)) return;
    setLookupMsg(null);
    lookupCnpj.mutate(cnpj, {
      onSuccess: (d: CnpjLookupResult) => {
        let preenchidos = 0;
        const seVazio = (atual: string, valor: string | null, set: (v: string) => void) => {
          if (atual.trim() === '' && valor && valor.trim() !== '') {
            set(valor);
            preenchidos += 1;
          }
        };
        seVazio(razaoSocial, d.razaoSocial, setRazaoSocial);
        seVazio(nomeFantasia, d.nomeFantasia, setNomeFantasia);
        seVazio(enderecoLogradouro, d.logradouro, setEnderecoLogradouro);
        seVazio(enderecoNumero, d.numero, setEnderecoNumero);
        seVazio(enderecoBairro, d.bairro, setEnderecoBairro);
        seVazio(enderecoCidade, d.municipio, setEnderecoCidade);
        seVazio(enderecoUf, d.uf, (v) => setEnderecoUf(v.toUpperCase().slice(0, 2)));
        seVazio(enderecoCep, d.cep, setEnderecoCep);
        seVazio(telefone, d.telefone, setTelefone);
        setLookupMsg(
          preenchidos > 0
            ? { tipo: 'ok', texto: `Dados encontrados — ${preenchidos} campo(s) vazio(s) preenchido(s).` }
            : { tipo: 'ok', texto: 'Dados encontrados — os campos já estavam preenchidos.' },
        );
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { message?: string } })?.response?.message ??
          'Não foi possível consultar o CNPJ agora. Tente de novo ou preencha manualmente.';
        setLookupMsg({ tipo: 'erro', texto: msg });
      },
    });
  }

  /**
   * C-08 (d) — busca de endereço pelo CEP (ViaCEP, via backend).
   *
   * Mesma regra do lookup de CNPJ: só preenche campo VAZIO. Sobrescrever o que
   * a pessoa digitou é a maneira mais rápida de fazer alguém desconfiar do
   * botão — e o número da porta, que nenhuma base pública sabe, seria o
   * primeiro a sumir.
   */
  function buscarPeloCep() {
    const digitos = enderecoCep.replace(/\D/g, '');
    if (digitos.length !== 8) return;
    setCepMsg(null);
    lookupCep.mutate(enderecoCep, {
      onSuccess: (d: CepLookupResult) => {
        let preenchidos = 0;
        const seVazio = (atual: string, valor: string | null, set: (v: string) => void) => {
          if (atual.trim() === '' && valor && valor.trim() !== '') {
            set(valor);
            preenchidos += 1;
          }
        };
        seVazio(enderecoLogradouro, d.logradouro, setEnderecoLogradouro);
        seVazio(enderecoBairro, d.bairro, setEnderecoBairro);
        seVazio(enderecoCidade, d.cidade, setEnderecoCidade);
        seVazio(enderecoUf, d.uf, (v) => setEnderecoUf(v.toUpperCase().slice(0, 2)));
        setCepMsg({
          tipo: 'ok',
          texto:
            preenchidos > 0
              ? `Endereço encontrado — ${preenchidos} campo(s) preenchido(s). Falta o número.`
              : 'Endereço encontrado — os campos já estavam preenchidos.',
        });
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { message?: string } })?.response?.message ??
          'Não foi possível consultar o CEP agora. Preencha o endereço à mão.';
        setCepMsg({ tipo: 'erro', texto: msg });
      },
    });
  }


  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>
          Cadastro completo do cliente — passo {step} de {seq.length}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          Identificação, endereço, contato e revisão — e as etapas das etiquetas que você marcar. Se
          você está no meio de uma proposta, o Cadastro rápido do Funil resolve sem sair de lá.
        </p>
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4 text-xs">
          {seq.map((_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded ${
                s <= step
                  ? 'bg-red'
                  : 'bg-line dark:bg-line-dark'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Identificação */}
        {passoAtual === 'ident' && (
          <div className="space-y-4" data-testid="wcli-step-1">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              1. Identificação básica
            </p>

            {/* F4: as etiquetas definem os passos e a régua de obrigatoriedade.
                Lista fechada — etiqueta nova nasce no Omie. */}
            <div>
              <Label required={etiquetaObrigatoria}>Etiquetas (segmento)</Label>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-1.5">
                As etiquetas definem as etapas: marcar Funcionário ou Fornecedor acrescenta um
                passo com os campos deles.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid="wcli-etiquetas">
                {etiquetasOfertadas.map((et) => (
                  <label key={et} className="inline-flex items-center gap-1.5 text-sm text-ink-2 dark:text-ink-dark-2">
                    <input
                      type="checkbox"
                      data-testid={`wcli-etiqueta-${et}`}
                      checked={etiquetasSelecionadas.includes(et)}
                      onChange={(e) =>
                        setEtiquetasSelecionadas((atual) =>
                          e.target.checked ? [...atual, et] : atual.filter((x) => x !== et),
                        )
                      }
                    />
                    {et}
                  </label>
                ))}
              </div>
            </div>

            {/* C-08 (c): pessoa física era impossível de cadastrar — o wizard só
                oferecia CNPJ, e sair sem documento nenhum quebra a busca por
                documento e a dedup. */}
            <div>
              <Label htmlFor="wcli-tipo-pessoa">Tipo de pessoa</Label>
              <Select
                id="wcli-tipo-pessoa"
                data-testid="wcli-tipo-pessoa"
                value={tipoPessoa}
                onChange={(e) => setTipoPessoa(e.target.value as TipoPessoa)}
                className="w-64"
              >
                <SelectOption value="juridica">Pessoa jurídica (CNPJ)</SelectOption>
                <SelectOption value="fisica">Pessoa física (CPF)</SelectOption>
              </Select>
            </div>

            {/* O par "Cliente direto × Via empresa terceirizada" e o select de
                empresa contratante saíram em 01/08: a IGM cobra sempre direto do
                condomínio. Não era escolha real — 0 de 760 clientes estavam
                marcados como terceirizados. */}

            <div>
              <span className="inline-flex items-center gap-1">
                <Label htmlFor="wcli-razao" required>
                  {tipoPessoa === 'fisica' ? 'Nome completo' : 'Razão social'}
                </Label>
                <HelpTip texto="Limite de 60 caracteres: o Omie, onde o cadastro é faturado, não aceita mais que isso. A busca pelo CNPJ traz o nome completo — se passar de 60, encurte mantendo o que identifica o cliente (o sistema sugere a abreviação no padrão da casa)." />
              </span>
              <Input
                id="wcli-razao"
                data-testid="wcli-razao"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                aria-invalid={!!validacao.erroDe('wcli-razao')}
                aria-describedby={validacao.erroDe('wcli-razao') ? 'wcli-razao-erro' : undefined}
              />
              {/* Limite do Omie (15/08): contador sempre visível; o valor NUNCA é
                  truncado — o campo exibe o nome inteiro e o salvar é que barra. */}
              <p
                className={`text-xs mt-0.5 ${razaoSocial.trim().length > LIMITE_RAZAO_SOCIAL_OMIE ? 'text-danger font-medium' : 'text-ink-3 dark:text-ink-dark-3'}`}
                data-testid="wcli-razao-contador"
              >
                {razaoSocial.trim().length}/{LIMITE_RAZAO_SOCIAL_OMIE}
              </p>
              <ErroCampo id="wcli-razao" mensagem={validacao.erroDe('wcli-razao')} />
              {razaoSocial.trim().length > LIMITE_RAZAO_SOCIAL_OMIE &&
                sugerirRazaoAbreviada(razaoSocial) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-1"
                    data-testid="wcli-razao-sugestao"
                    onClick={() => setRazaoSocial(sugerirRazaoAbreviada(razaoSocial) as string)}
                  >
                    Usar sugestão: {sugerirRazaoAbreviada(razaoSocial)}
                  </Button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wcli-fantasia">
                  {tipoPessoa === 'fisica' ? 'Como é conhecido' : 'Nome fantasia'}
                </Label>
                <Input
                  id="wcli-fantasia"
                  value={nomeFantasia}
                  onChange={(e) => setNomeFantasia(e.target.value)}
                />
              </div>
              {tipoPessoa === 'fisica' ? (
                <div>
                  <Label htmlFor="wcli-cpf" required={docObrigatorio}>CPF</Label>
                  <Input
                    id="wcli-cpf"
                    data-testid="wcli-cpf"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    placeholder="000.000.000-00"
                  />
                  {!cpfEmpty && !cpfValid && (
                    <p className="text-xs text-danger mt-1" data-testid="wcli-cpf-error">
                      CPF inválido — confira os dígitos.
                    </p>
                  )}
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                    A consulta automática da Receita só existe para CNPJ — os dados da pessoa
                    física são preenchidos à mão.
                  </p>
                </div>
              ) : (
                <div>
                  <span className="inline-flex items-center gap-1">
                    <Label htmlFor="wcli-cnpj" required={docObrigatorio}>CNPJ</Label>
                    <HelpTip texto="Digite o CNPJ e clique em 'Buscar dados pelo CNPJ' para preencher automaticamente razão social, endereço e atividade (CNAE) a partir da base pública da Receita. Só completa os campos vazios — não sobrescreve o que você já digitou." />
                  </span>
                  <Input
                    id="wcli-cnpj"
                    data-testid="wcli-cnpj"
                    value={cnpj}
                    onChange={(e) => setCnpj(e.target.value)}
                    placeholder="00.000.000/0001-00"
                  />
                  {!cnpjEmpty && !cnpjValid && (
                    <p className="text-xs text-danger mt-1" data-testid="wcli-cnpj-error">
                      CNPJ inválido — confira os dígitos.
                    </p>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    disabled={!isValidCNPJ(cnpj) || lookupCnpj.isPending}
                    loading={lookupCnpj.isPending}
                    onClick={buscarDadosCnpj}
                    data-testid="wcli-lookup-cnpj"
                  >
                    Buscar dados pelo CNPJ
                  </Button>
                  {lookupMsg && (
                    <p
                      className={`text-xs mt-1 ${lookupMsg.tipo === 'ok' ? 'text-ink-3 dark:text-ink-dark-3' : 'text-danger'}`}
                      data-testid="wcli-lookup-msg"
                    >
                      {lookupMsg.texto}
                    </p>
                  )}
                </div>
              )}
            </div>

            {clienteExistente && (
              <Alert variant="warning" rail data-testid="wcli-dedup-alert">
                <strong>
                  Este {tipoPessoa === 'fisica' ? 'CPF' : 'CNPJ'} já está cadastrado:
                </strong>{' '}
                {clienteExistente.razaoSocial}. Deseja{' '}
                <a
                  href={`/admin/clientes-v2/${clienteExistente.id}`}
                  className="underline"
                >
                  abrir o cadastro existente
                </a>{' '}
                ou continuar mesmo assim?
              </Alert>
            )}

            {/* P3 (AJUSTES-POS-2): o Segmento saiu do cadastro. Segmento é a
                TAG do Omie — não se define à mão aqui. Depois de criar, o
                segmento é escolhido na ficha do cliente (aba Identificação),
                entre os valores que já vieram do Omie. */}
          </div>
        )}

        {/* Step 2: Endereço */}
        {passoAtual === 'endereco' && (
          <div className="space-y-4" data-testid="wcli-step-2">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              2. Endereço fiscal
            </p>
            {/* C-08 (d): o CEP vem primeiro porque agora ele PUXA o resto. */}
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <span className="inline-flex items-center gap-1">
                  <Label htmlFor="wcli-cep">CEP</Label>
                  <HelpTip texto="Digite o CEP e clique em 'Buscar pelo CEP': logradouro, bairro, cidade e UF vêm da base dos Correios. Só completa campo vazio — e o número da porta continua sendo digitado por você." />
                </span>
                <Input
                  id="wcli-cep"
                  data-testid="wcli-cep"
                  value={enderecoCep}
                  onChange={(e) => setEnderecoCep(e.target.value)}
                  placeholder="00000-000"
                  className="w-40"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={enderecoCep.replace(/\D/g, '').length !== 8 || lookupCep.isPending}
                loading={lookupCep.isPending}
                onClick={buscarPeloCep}
                data-testid="wcli-lookup-cep"
              >
                Buscar pelo CEP
              </Button>
            </div>
            {!cepValid && (
              <p className="text-xs text-danger" data-testid="wcli-cep-error">
                CEP incompleto — são 8 dígitos.
              </p>
            )}
            {cepMsg && (
              <p
                className={`text-xs ${cepMsg.tipo === 'ok' ? 'text-ink-3 dark:text-ink-dark-3' : 'text-danger'}`}
                data-testid="wcli-cep-msg"
              >
                {cepMsg.texto}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-3">
                <Label htmlFor="wcli-log">Logradouro</Label>
                <Input
                  id="wcli-log"
                  value={enderecoLogradouro}
                  onChange={(e) => setEnderecoLogradouro(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="wcli-num">Número</Label>
                <Input
                  id="wcli-num"
                  value={enderecoNumero}
                  onChange={(e) => setEnderecoNumero(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="wcli-bai">Bairro</Label>
                <Input
                  id="wcli-bai"
                  value={enderecoBairro}
                  onChange={(e) => setEnderecoBairro(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="wcli-cid">Cidade</Label>
                <Input
                  id="wcli-cid"
                  value={enderecoCidade}
                  onChange={(e) => setEnderecoCidade(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="wcli-uf">UF</Label>
                <Input
                  id="wcli-uf"
                  value={enderecoUf}
                  onChange={(e) => setEnderecoUf(e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                  placeholder="SP"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Contato */}
        {passoAtual === 'contato' && (
          <div className="space-y-4" data-testid="wcli-step-3">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              3. Contato + observações
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wcli-email" required={emailObrigatorio}>E-mail</Label>
                <Input
                  id="wcli-email"
                  data-testid="wcli-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {!emailValid && (
                  <p className="text-xs text-danger mt-1" data-testid="wcli-email-error">
                    E-mail inválido — confira o formato.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="wcli-tel" required={telefoneObrigatorio}>Telefone</Label>
                <Input
                  id="wcli-tel"
                  data-testid="wcli-tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="(11) 99999-9999"
                />
                {!telefoneValid && (
                  <p className="text-xs text-danger mt-1" data-testid="wcli-tel-error">
                    Telefone incompleto — use DDD + número.
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="wcli-obs">Observações operacionais</Label>
              <Input
                id="wcli-obs"
                value={observacoesOperacionais}
                onChange={(e) => setObservacoesOperacionais(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="wcli-unidades" required={unidadesObrigatorio}>Número de unidades (condomínio)</Label>
              <Input
                id="wcli-unidades"
                data-testid="wcli-unidades"
                type="number"
                min={0}
                value={numUnidades}
                onChange={(e) => setNumUnidades(e.target.value)}
                placeholder="opcional"
                className="w-40"
              />
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                Usado no orçamento para mostrar o valor por unidade por mês nos simuladores.
              </p>
            </div>
          </div>
        )}

        {/* Passos dinâmicos: etiquetas marcadas */}
        {(passoAtual === 'funcionario' || passoAtual === 'fornecedor') && (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 mb-2">
              <h3 className="font-medium text-ink-1 dark:text-ink-dark-1">
                {passoAtual === 'funcionario' ? 'Dados de funcionário' : 'Regras de fornecedor'}
              </h3>
              <HelpTip texto={passoAtual === 'funcionario' ? AJUDA_BLOCO_FUNCIONARIO : AJUDA_BLOCO_FORNECEDOR} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              {(passoAtual === 'funcionario' ? CAMPOS_BLOCO_FUNCIONARIO : CAMPOS_BLOCO_FORNECEDOR).map((c) => (
                <CampoCelula
                  key={c.name}
                  campo={c}
                  bloco={passoAtual}
                  exigido={exigidos.has(c.name)}
                  editando
                  valor={null}
                  rascunho={extras[c.name] ?? ''}
                  onChange={(v) => setExtras((r) => ({ ...r, [c.name]: v }))}
                  onPreencherOutros={(campos) => setExtras((r) => ({ ...r, ...campos }))}
                />
              ))}
            </div>
          </div>
        )}

        {/* Passo dinâmico: exigidos pela régua que o wizard não mostra fixo */}
        {passoAtual === 'exigidos' && (
          <div className="space-y-1" data-testid="wcli-step-exigidos">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-2">
              {step}. Campos exigidos pela empresa
            </p>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
              Estes campos foram marcados como obrigatórios em Configurações › Preenchimento
              obrigatório — o cadastro só nasce com eles preenchidos.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              {specsExtras.map((c) => (
                <CampoCelula
                  key={c.name}
                  campo={c}
                  bloco="exigidos"
                  exigido
                  editando
                  valor={null}
                  rascunho={extras[c.name] ?? ''}
                  onChange={(v) => setExtras((r) => ({ ...r, [c.name]: v }))}
                  onPreencherOutros={(campos) => setExtras((r) => ({ ...r, ...campos }))}
                />
              ))}
            </div>
          </div>
        )}

        {passoAtual === 'revisao' && (
          <div className="space-y-4" data-testid="wcli-step-4">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              {step}. Revisão + Salvar
            </p>
            <div className="bg-paper-warm dark:bg-paper-dark-warm p-4 rounded-md space-y-2 text-sm">
              <p>
                <strong>{tipoPessoa === 'fisica' ? 'Nome:' : 'Razão social:'}</strong>{' '}
                {razaoSocial}
                {nomeFantasia && ` (${nomeFantasia})`}
              </p>
              {tipoPessoa === 'fisica'
                ? cpf && (
                    <p>
                      <strong>CPF:</strong> {cpf}
                    </p>
                  )
                : cnpj && (
                    <p>
                      <strong>CNPJ:</strong> {cnpj}
                    </p>
                  )}
              <p>
                <strong>Tipo:</strong>{' '}
                <Badge variant="success" size="sm">
                  Direto
                </Badge>
              </p>
              {etiquetasSelecionadas.length > 0 && (
                <p>
                  <strong>Etiquetas:</strong> {etiquetasSelecionadas.join(', ')}
                </p>
              )}
              {enderecoCidade && (
                <p>
                  <strong>Cidade:</strong> {enderecoCidade}
                  {enderecoUf && `/${enderecoUf}`}
                </p>
              )}
              {email && (
                <p>
                  <strong>Email:</strong> {email}
                </p>
              )}
              {telefone && (
                <p>
                  <strong>Telefone:</strong> {telefone}
                </p>
              )}
            </div>

            <Alert variant="info" rail>
              <strong>Cliente direto:</strong> o cadastro nasce aqui no sistema. Se o
              cliente também existir no Omie, o vínculo acontece pela sincronização —
              nada é enviado ao Omie.
            </Alert>

            {create.error && (
              <Alert variant="danger" rail data-testid="wcli-submit-error">
                {create.error.message}
              </Alert>
            )}
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        {step > 1 && (
          <Button variant="ghost" onClick={handleBack}>
            ← Voltar
          </Button>
        )}
        {step < seq.length ? (
          <Button onClick={handleNext} data-testid="wcli-next">
            Avançar →
          </Button>
        ) : (
          <Button
            loading={create.isPending}
            onClick={handleSubmit}
            data-testid="wcli-submit"
          >
            Criar cliente
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

function normalize(s: string): string {
  return s.replace(/\D/g, '');
}
